import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {
  handleFormNumber,
  reverseConvertUnit,
  useTaskForm,
  useTaskGroupSelect,
  useMeasurementMethodSelect,
  useRestaurantListField,
  valueExists
} from '../../../config/utils'
import {useAppState} from '../../../state'
import {DishwasherTemperatureTaskResponse} from '../../../state/rest'
import {Button, ButtonRowGrid, ButtonRowWrap, IconButton} from '../../Atoms/Buttons'
import {BigLabelInput, LabelInput, Select, Toggler} from '../../Atoms/Forms'
import {Baseline, Grid, GridSpan} from '../../Layout/Grid'
import {MainContent, MainLayoutWithoutStretch, RightSidebar} from '../../Layout/Layout'
import LocationsList from '../../Organisms/Locations/LocationsList'
import {HeaderActions, HeaderRow} from '../../Molecules/ViewComponents/ViewHeader'
import {TasksGoBackHeaderTitle, CommonInformationFields} from './CommonTasksComponents'
import TaskMenuModal from './TaskMenuModal'
import Scheduler from '../../Organisms/Scheduler/Scheduler'
import {
  DISHWASHER_TEMPERATURE_DEFAULTS,
  DishwasherConfiguration,
  DishwasherTemperatureType
} from './dishwasherTemperatureDefaults'
import {IconRemoveItem} from '../../../Assets/Icons/LargeIcons'
import styled from 'styled-components'

interface Props {
  savedData?: DishwasherTemperatureTaskResponse
  backPath?: string
}

const createConfiguration = (): DishwasherConfiguration => ({
  name: '',
  temperatures: DISHWASHER_TEMPERATURE_DEFAULTS.map(temperature => ({
    type: temperature.type,
    minValue: temperature.minValue.toString(),
    maxValue: temperature.maxValue.toString()
  }))
})

const CreateDishwasherTemperatureTask = ({savedData, backPath}: Props) => {
  const {t} = useTranslation('tasks')
  const {state} = useAppState()
  const isEditing = !!savedData?.id
  const isDuplicate = !!savedData && !savedData.id
  const saved = savedData
    ? {
        ...savedData,
        name:
          isDuplicate && savedData.name
            ? `${t('tasks:labels.copyOf', 'Copy of')} ${savedData.name.toLowerCase()}`
            : savedData.name,
        configurations: savedData.dishwasherConfigurations || [],
        sites: isDuplicate ? [state.site!] : savedData.sites || [state.site!]
      }
    : undefined
  const [configurations, setConfigurations] = useState<DishwasherConfiguration[]>(
    saved?.configurations.length
      ? saved.configurations.map(configuration => ({
          name:
            isDuplicate && configuration.name
              ? `${t('tasks:labels.copyOf', 'Copy of')} ${configuration.name.toLowerCase()}`
              : configuration.name,
          temperatures: configuration.temperatures.map(temperature => ({
            ...temperature,
            minValue: temperature.minValue.toString(),
            maxValue: temperature.maxValue.toString()
          }))
        }))
      : [createConfiguration()]
  )
  const fc = useTaskForm('dishwasherTemperature', saved)
  const rc = useRestaurantListField({watch: fc.watch, setValue: fc.setValue}, isDuplicate ? undefined : saved?.sites)
  const {taskGroupOptions, selectTaskGroup, selectedTaskGroup} = useTaskGroupSelect(
    'dishwasherTemperature',
    saved?.taskGroup
  )
  const {selectMethod, selectedMeasurementMethod, measurementOptions} = useMeasurementMethodSelect(
    'device',
    saved?.measurementMethod
  )

  const updateConfiguration = (index: number, update: Partial<DishwasherConfiguration>) => {
    setConfigurations(current =>
      current.map((configuration, currentIndex) =>
        currentIndex === index ? {...configuration, ...update} : configuration
      )
    )
  }

  const updateTemperature = (
    configurationIndex: number,
    type: DishwasherTemperatureType,
    field: 'minValue' | 'maxValue',
    value: string
  ) => {
    setConfigurations(current =>
      current.map((configuration, index) =>
        index === configurationIndex
          ? {
              ...configuration,
              temperatures: configuration.temperatures.map(temperature =>
                temperature.type === type ? {...temperature, [field]: value} : temperature
              )
            }
          : configuration
      )
    )
  }

  const toggleTemperature = (configurationIndex: number, type: DishwasherTemperatureType) => {
    setConfigurations(current =>
      current.map((configuration, index) => {
        if (index !== configurationIndex) return configuration
        const existing = configuration.temperatures.find(temperature => temperature.type === type)
        if (existing)
          return {
            ...configuration,
            temperatures: configuration.temperatures.filter(temperature => temperature.type !== type)
          }
        const defaultTemperature = DISHWASHER_TEMPERATURE_DEFAULTS.find(temperature => temperature.type === type)!
        return {
          ...configuration,
          temperatures: [
            ...configuration.temperatures,
            {type, minValue: defaultTemperature.minValue.toString(), maxValue: defaultTemperature.maxValue.toString()}
          ]
        }
      })
    )
  }

  const isValid =
    configurations.length > 0 &&
    configurations.every(
      configuration =>
        configuration.name.trim() &&
        configuration.temperatures.length > 0 &&
        configuration.temperatures.every(
          temperature =>
            valueExists(temperature.minValue) &&
            valueExists(temperature.maxValue) &&
            Number(temperature.minValue) < Number(temperature.maxValue)
        )
    ) &&
    rc.list.length > 0

  const commonPayload = {
    taskGroup: selectedTaskGroup,
    measurementMethod: selectedMeasurementMethod,
    unit: state.site!.temperatureUnit,
    sites: rc.list,
    assets: fc.assets,
    lateByDays: fc.lateByDays
  }
  const dishwasherConfigurations = configurations.map(configuration => ({
    name: configuration.name,
    temperatures: configuration.temperatures.map(temperature => ({
      type: temperature.type,
      minValue: reverseConvertUnit(state.site!, handleFormNumber(temperature.minValue)),
      maxValue: reverseConvertUnit(state.site!, handleFormNumber(temperature.maxValue))
    }))
  }))
  const payloads = dishwasherConfigurations.map(configuration => ({
    ...commonPayload,
    name: configuration.name,
    alarmsEnabled: true,
    dishwasherConfigurations: [configuration]
  }))

  return (
    <MainLayoutWithoutStretch>
      <MainContent variant="white">
        <HeaderRow
          modal={
            isEditing ? (
              <TaskMenuModal
                isOpen={fc.modalOpen}
                onClose={() => fc.setModalOpen(false)}
                onDelete={fc.editingDisabled ? undefined : fc.remove}
                onDuplicate={fc.duplicate}
              />
            ) : (
              undefined
            )
          }
        >
          <TasksGoBackHeaderTitle
            label={
              isEditing
                ? t('tasks:labels.editTask.dishwasherTemperature', 'Edit dishwasher temperature task')
                : t('tasks:labels.newTask.dishwasherTemperature', 'Add dishwasher temperature task')
            }
            isEditing={isEditing}
            backPath={backPath}
            backLabel={backPath ? t('tasks:labels.selectApplianceType', 'Select appliance type') : undefined}
          />
          {isEditing && <HeaderActions onMenu={() => fc.setModalOpen(true)} />}
        </HeaderRow>
        <Baseline>
          <ConfigurationCardRow>
            {t(
              'tasks:labels.dishwasher.selectDescription',
              'Configure one or more dishwashers and their monitored temperature points.'
            )}
          </ConfigurationCardRow>

          {configurations.map((configuration, index) => (
            <ConfigurationCard key={index}>
              <DishwasherNameInput>
                <BigLabelInput
                  required
                  labelText={t('tasks:labels.taskName', 'Task name')}
                  value={configuration.name}
                  placeholder={t('tasks:placeholders.newTaskName', 'New task name')}
                  onChange={event => updateConfiguration(index, {name: event.target.value})}
                />
              </DishwasherNameInput>
              <TemperatureHeading>{t('tasks:labels.dishwasher.temperature', 'Temperature')}</TemperatureHeading>
              {DISHWASHER_TEMPERATURE_DEFAULTS.map(defaultTemperature => {
                const temperature = configuration.temperatures.find(item => item.type === defaultTemperature.type)
                return temperature ? (
                  <TemperatureRow key={defaultTemperature.type}>
                    <span>{t(defaultTemperature.labelKey, defaultTemperature.label)}</span>
                    <LabelInput
                      required
                      type="number"
                      labelText={`${t('common:general.alarmMinLimit', 'Alarm min limit')} (°C)`}
                      errorMsg={
                        valueExists(temperature.maxValue) &&
                        valueExists(temperature.minValue) &&
                        Number(temperature.minValue) >= Number(temperature.maxValue)
                          ? (t('common:validation.valueTooHigh', 'Value must be lower than max value') as string)
                          : undefined
                      }
                      value={temperature.minValue}
                      onChange={event =>
                        updateTemperature(index, defaultTemperature.type, 'minValue', event.target.value)
                      }
                    />
                    <LabelInput
                      required
                      type="number"
                      labelText={`${t('common:general.alarmMaxLimit', 'Alarm max limit')} (°C)`}
                      errorMsg={
                        valueExists(temperature.maxValue) &&
                        valueExists(temperature.minValue) &&
                        Number(temperature.minValue) >= Number(temperature.maxValue)
                          ? (t('common:validation.valueTooLow', 'Value must be higher than min value') as string)
                          : undefined
                      }
                      value={temperature.maxValue}
                      onChange={event =>
                        updateTemperature(index, defaultTemperature.type, 'maxValue', event.target.value)
                      }
                    />
                    <IconButton
                      icon={<IconRemoveItem width="2rem" height="2rem" />}
                      buttonProps={{
                        onClick: () => toggleTemperature(index, defaultTemperature.type),
                        'aria-label': t('tasks:buttons.removeTemperature', 'Remove temperature')
                      }}
                    />
                  </TemperatureRow>
                ) : null
              })}
              <TemperatureActions>
                {DISHWASHER_TEMPERATURE_DEFAULTS.filter(
                  defaultTemperature =>
                    !configuration.temperatures.some(temperature => temperature.type === defaultTemperature.type)
                ).map(defaultTemperature => (
                  <button
                    type="button"
                    key={defaultTemperature.type}
                    onClick={() => toggleTemperature(index, defaultTemperature.type)}
                  >
                    {`${t('tasks:buttons.add', 'Add')} ${t(defaultTemperature.labelKey, defaultTemperature.label)}`}
                  </button>
                ))}
              </TemperatureActions>
              {configurations.length > 1 && (
                <RemoveDishwasherButton
                  type="button"
                  onClick={() =>
                    setConfigurations(current => current.filter((_, currentIndex) => currentIndex !== index))
                  }
                >
                  {t('tasks:buttons.removeDishwasher', 'Remove dishwasher')}
                </RemoveDishwasherButton>
              )}
            </ConfigurationCard>
          ))}
          {!isEditing && (
            <AddDishwasherButton
              type="button"
              onClick={() => setConfigurations(current => [...current, createConfiguration()])}
            >
              {t('tasks:buttons.addDishwasher', 'Add dishwasher')}
            </AddDishwasherButton>
          )}
          <Grid style={{padding: '1.5rem'}}>
            <Baseline>
              <Select
                returnId
                nativeProps={{
                  name: 'taskGroup',
                  onChange: event => selectTaskGroup(event.target.value),
                  value: selectedTaskGroup.id
                }}
                id="taskGroup"
                label={t('tasks:labels.taskGroup', 'Task group')}
                options={taskGroupOptions}
              />
              <Select
                returnId
                nativeProps={{
                  name: 'measurementMethod',
                  onChange: event => selectMethod(event.target.value),
                  value: selectedMeasurementMethod.id
                }}
                id="measurementMethod"
                label={t('tasks:labels.measurementMethod', 'Measurement method')}
                options={measurementOptions}
              />
              <Toggler
                label={t('tasks:labels.scheduledTask', 'Scheduled task')}
                values={[t('common:togglerValues.no', 'No'), t('common:togglerValues.yes', 'Yes')]}
                initiallyChecked={!!saved && !!saved.schedule}
                name="isScheduledTask"
                onClick={() => fc.toggleShow()}
              />
              {!!fc.scheduledPayload && (
                <Scheduler
                  onChange={fc.handleScheduler}
                  schedule={saved ? fc.reverseSchedule(saved.schedule) : undefined}
                  lateByDays={fc.lateByDays}
                  onLateByDaysChange={fc.setLateByDays}
                />
              )}
            </Baseline>
            <Baseline>
              <CommonInformationFields useForm={fc} />
            </Baseline>
          </Grid>
          <ButtonRowWrap>
            <div />
            <ButtonRowGrid>
              <Button name="cancel-button" negative variant="secondary" disabled={fc.loading} onClick={fc.cancel}>
                {t('common:buttons.cancel', 'Cancel')}
              </Button>
              <Button
                variant="secondary"
                disabled={!isValid || fc.loading || fc.editingDisabled}
                onClick={() =>
                  isEditing
                    ? fc.update({...payloads[0], name: configurations[0].name})
                    : fc.submitBulkEquipmentTemperature(payloads, commonPayload)
                }
              >
                {t('common:buttons.save', 'Save')}
              </Button>
              {fc.editingDisabled && (
                <GridSpan span={2}>
                  <p>
                    {t(
                      'tasks:messages.taskInUseMessage',
                      'This task is in use for locations you don’t have access to. You can not edit or delete it but you can duplicate the task to create a new one'
                    )}
                  </p>
                </GridSpan>
              )}
            </ButtonRowGrid>
          </ButtonRowWrap>
        </Baseline>
      </MainContent>
      <RightSidebar>
        <LocationsList
          controller={rc}
          cyKey="dishwasher-task"
          label={t('tasks:labels.selectLocation', 'Select which locations use this task')}
        />
      </RightSidebar>
    </MainLayoutWithoutStretch>
  )
}

export default CreateDishwasherTemperatureTask

const ConfigurationCard = styled.section`
  border: 1px solid #e0e3e8;
  padding: 1.5rem;
  margin: 1.5rem;
`
const ConfigurationCardRow = styled.section`
  padding-left: 1.5rem;
`

const DishwasherNameInput = styled.div`
  max-width: 32rem;
  margin-bottom: 1.5rem;
  input {
    border: 1px solid #8a929d;
    border-radius: 4px;
    padding: 0.75rem;
  }
`

const TemperatureHeading = styled.strong`
  display: block;
  margin-bottom: 0.75rem;
`

const TemperatureActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
`

const AddDishwasherButton = styled.button`
  margin: 0 0 1.5rem 1.5rem;
`
const RemoveDishwasherButton = styled.button`
  margin: 1.5rem 0 1.5rem 0;
`

const TemperatureRow = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 0fr) 140px 140px 40px;
  gap: 1rem;
  align-items: end;
  margin-top: 1rem;

  > span {
    align-self: center;
  }
`
